

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import "../../../style/draftlist.css";
import "../../../style/dashboardcentralhubstyle.css";
import "../../../style/customresponsivefile.css";
import { apiAxios } from "../../../utils/apiAxios";

interface Draft {
  draftId: number;
  patientId: number | null;
  patientName: string | null;
  totalAmount: number | null;
  createdAt: string;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const DraftList: React.FC = () => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    try {
      setLoading(true);

      const storedClinic = sessionStorage.getItem("selectedClinic");

      if (storedClinic) {
        const parsedClinic = JSON.parse(storedClinic);

        const res = await apiAxios.get(
          `${API_BASE}/Dentist/draft-list/${parsedClinic.clinicID}`
        );

        setDrafts(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching drafts:", error);
      toast.error("Failed to load drafts.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedDraftId) return;

    try {
      const response = await apiAxios.get(
        `${API_BASE}/Dentist/draft-delete/${selectedDraftId}`
      );

      if (response.status === 200) {
        toast.success("Draft deleted successfully!");
        fetchDrafts();
      } else {
        toast.error("Failed to delete draft.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    } finally {
      setShowDeleteModal(false);
      setSelectedDraftId(null);
    }
  };

  const columns: TableColumn<Draft>[] = [
    {
      name: "Patient",
      selector: row => row.patientName ?? "-",
      sortable: true,
      center: true,
      grow: 2,
    },
    {
      name: "Total Amount",
      cell: row => (
        <span className="fw-semibold">
          ₹ {row.totalAmount ?? 0}
        </span>
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Created Date",
      cell: row =>
        new Date(row.createdAt).toLocaleDateString("en-GB"),
      sortable: true,
       center: true,
    },
    {
      name: "Actions",
      width: "180px",
      cell: row => (
        <div className="d-flex gap-2">
          <div
            className="action-btn delete custom-edit-icon"
            onClick={() => {
              sessionStorage.setItem(
                "draftId",
                row.draftId.toString()
              );

              navigate(`/dentist/dashboard/${row.draftId}`);
            }}
          >
            <i
              className="bi bi-pencil-square"
              title="Edit"
            ></i>
          </div>

          <div
            className="action-btn delete custom-edit-icon"
            onClick={() => {
              setSelectedDraftId(row.draftId);
              setShowDeleteModal(true);
            }}
          >
            <i
              className="bi bi-trash"
              title="Delete"
            ></i>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="mt-4">
      <h4 className="mb-4">Saved Drafts</h4>

      <div className="premium-card p-4 mt-4">
        <h5 className="mb-4">Draft List</h5>

        <DataTable
          columns={columns}
          data={drafts}
          pagination
          paginationPerPage={6}
          paginationRowsPerPageOptions={[6, 10, 20, 50]}
          highlightOnHover
          striped
          responsive
          progressPending={loading}
          noDataComponent="No drafts found."
          className="admin-delivery-table"
        />
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        dialogClassName="custom-top-modal"
      >
        <Modal.Body className="text-center">
          <p className="fs-6">
            Are you sure you want to delete this draft?
          </p>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <button
            type="button"
            className="btn py-2 px-3 btn-primary"
            onClick={confirmDelete}
          >
            Yes
          </button>

          <button
            type="button"
            className="btn py-2 px-3 btn-secondary mx-2"
            onClick={() => setShowDeleteModal(false)}
          >
            No
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DraftList;